import { useEffect } from 'react'

import { useRequest } from 'ahooks'
import { isFunction } from 'lodash'

import { queryDeptUserTreeApi } from '@eastcoal/api/auth/dept/queryDeptUserTree'

import { KeySet } from '@eastcoal/common/types/utilities'

import { useMemberDeptUserStoreApi } from '../hooks/useStore'
import {
  MemberDeptUserBaseAction,
  MemberDeptUserStore
} from '../store/interface'

import { MemberDeptUserStoreUpdaterProps } from './interface'

const useStoreUpdater = <M, K extends KeySet<MemberDeptUserStore<M>>>(
  key: K,
  value?: MemberDeptUserStore<M>[K],
  condition?: boolean
) => {
  const { setState } = useMemberDeptUserStoreApi()

  useEffect(() => {
    if (condition !== false) {
      setState({ [key]: value })
    }
  }, [key, value, condition, setState])
}

const useStoreSetter = <
  M extends boolean,
  K extends KeySet<MemberDeptUserBaseAction<M>>
>(
  setter: K,
  value: unknown
) => {
  const { getState } = useMemberDeptUserStoreApi()
  useEffect(() => {
    const setterFn = getState()[setter]
    if (isFunction(setterFn)) {
      setterFn(value as any)
    }
  }, [setter, value, getState])
}

export const MemberDeptUserStoreUpdater = <M>({
  /* ---------------------------------------------------------------------------- */
  /*                                MemberDeptUserStore                             */
  /* ---------------------------------------------------------------------------- */
  multiple,
  value,
  onChange,

  /* -------------------------------------------------------------------------- */
  /*                               Api Parameters                               */
  /* -------------------------------------------------------------------------- */
  memberId,
  deptId
}: MemberDeptUserStoreUpdaterProps<M>) => {
  const { data, error, loading, refreshAsync } = useRequest(
    () =>
      queryDeptUserTreeApi({
        memberId,
        deptId
      }),
    {
      manual: false,
      ready: !!memberId,
      refreshDeps: [memberId, deptId]
    }
  )

  useStoreUpdater('data', data, data !== undefined)
  useStoreUpdater('error', error)
  useStoreUpdater('loading', loading)
  useStoreUpdater('reload', refreshAsync)

  useStoreUpdater('multiple', multiple)
  useStoreUpdater<M, 'onChange'>('onChange', onChange)

  useStoreSetter('setValue', value)

  return null
}
